import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { Swiper, SwiperSlide } from 'swiper/react'; // eslint-disable-line import/no-unresolved
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import GradientDivider from '../gradientDivider/gradientDivider';
import FeatureHighlightItem from './featureHighlightItem';
import styles from './featureHighlight.module.scss';
import { getResponsiveImageProps } from '../../utils/optimizedContentfulImage';

export default function FeatureHighlight({
  eyebrow = '',
  eyebrowMobile = '',
  alignment = 'left',
  items = [],
}) {
  const [activeIndex, setActiveIndex] = useState(0);

  const handleActivate = useCallback((index) => {
    setActiveIndex(index);
  }, []);

  if (!items || items.length === 0) {
    return null;
  }

  const sectionClass = `${styles.section} ${
    alignment === 'right' ? styles.alignmentRight : ''
  }`.trim();

  return (
    <>
      {eyebrowMobile && (
        <div className={styles.mobileEyebrowWrapper}>
          <h2 className={styles.mobileEyebrow}>{parse(eyebrowMobile)}</h2>
        </div>
      )}
      <section
        aria-label={eyebrow || 'Feature highlights'}
        className={sectionClass}
      >
        {/* Stacked-row layout (>= tablet-md). Hidden via CSS below the breakpoint. */}
        <div className={styles.list}>
          <div className={styles.bgStack} aria-hidden="true">
            {items.map((item, index) => (
              <img
                key={item.headline}
                data-testid="bg-image"
                {...getResponsiveImageProps(item.backgroundImage?.url, {
                  widths: [400, 800, 1200, 1600],
                  sizes: '(max-width: 768px) 100vw, 1200px',
                })}
                alt={index === activeIndex ? item.headline : ''}
                className={`${styles.bgImage} ${
                  index === activeIndex ? styles.bgImageActive : ''
                }`.trim()}
              />
            ))}
          </div>
          <div className={styles.overlay} aria-hidden="true" />
          <div className={styles.listContent}>
            {eyebrow && <p className={styles.listEyebrow}>{eyebrow}</p>}
            {items.map((item, index) => (
              <div key={item.headline} className={styles.row}>
                <FeatureHighlightItem
                  headline={item.headline}
                  bullets={item.bullets}
                  index={index}
                  isActive={index === activeIndex}
                  onActivate={handleActivate}
                />
                {index < items.length - 1 && <GradientDivider />}
              </div>
            ))}
          </div>
        </div>

        {/* Carousel layout (< tablet-md). Hidden via CSS at and above the breakpoint. */}
        <div className={styles.carousel}>
          <Swiper
            className={styles.swiper}
            modules={[Pagination]}
            slidesPerView={1.25}
            spaceBetween={20}
            pagination={{ clickable: true }}
          >
            {items.map((item) => (
              <SwiperSlide key={item.headline}>
                <div
                  role="group"
                  aria-label={item.headline}
                  className={styles.slide}
                >
                  <img
                    className={styles.slideBgImage}
                    {...getResponsiveImageProps(item.backgroundImage?.url, {
                      widths: [400, 800, 1200],
                      sizes: '100vw',
                    })}
                    alt=""
                    aria-hidden="true"
                  />
                  <div className={styles.slideOverlay} aria-hidden="true" />
                  <div className={styles.slideContent}>
                    <h3 className={styles.slideHeadline}>{item.headline}</h3>
                    <div className={styles.slideBullets}>
                      {parse(item.bullets || '')}
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>
    </>
  );
}

FeatureHighlight.propTypes = {
  eyebrow: PropTypes.string,
  eyebrowMobile: PropTypes.string,
  alignment: PropTypes.oneOf(['left', 'right']),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      headline: PropTypes.string.isRequired,
      bullets: PropTypes.string,
      backgroundImage: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
      }),
    })
  ),
};
